import ReactDOMServer from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";

import { watermarkService } from "app/common/services/watermarkService";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import type { IChartData } from "app/pages/website-analysis/social-overview/socialTraffic/chartData";
import type { ISocialTrafficContext } from "app/pages/website-analysis/social-overview/socialTraffic/context";
import { Tooltip } from "app/pages/website-analysis/social-overview/socialTraffic/Tooltip";
import {
    percentDataFormat,
    percentsFilter,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";

export const getChartConfig = (
    socialTrafficContext: ISocialTrafficContext,
    categories: Array<string>,
    filteredChartData: IChartData,
) => {
    const { chosenSites } = socialTrafficContext;
    const isCompare = chosenSites.length > 1;
    const config = {
        chart: {
            height: 198,
            type: "column",
            animation: false,
            spacingTop: 20,
            events: {
                load: function () {
                    watermarkService.add.call(this, { opacity: 0.1 });
                },
            },
        },
        xAxis: {
            categories,
            lineColor: colorsPalettes.carbon[100],
            gridLineColor: colorsPalettes.carbon[50],
            crosshair: isCompare,
            tickWidth: 0,
            labels: {
                align: "center",
                rotation: 0,
                useHTML: true,
                style: {
                    fontSize: "14px",
                    textAlign: "center",
                    fontFamily: '"Roboto", sans-serif',
                    minWidth: "100px",
                },
                formatter: function () {
                    const socialMedia = this.value;
                    const favicon = filteredChartData[0].data.find(({ key }) => key === socialMedia)?.favicon;
                    return favicon
                        ? `<img src=${favicon} style="width: 16px; height: 16px; padding: 0px 4px 2px 0px;">${socialMedia}`
                        : socialMedia;
                },
            },
        },
        yAxis: {
            gridLineColor: "#e4e4e4",
            min: 0,
            max: 100,
            startOnTick: false,
            endOnTick: true,
            title: null,
            opposite: false,
            showLastLabel: true,
            color: colorsPalettes.carbon[400],
            labels: {
                align: "right",
                enabled: true,
                useHTML: true,
                x: -10,
                formatter() {
                    return this.value + percentDataFormat;
                },
                style: {
                    fontSize: "12px",
                    textTransform: "uppercase",
                    fontFamily: '"Roboto", sans-serif',
                    color: colorsPalettes.carbon[400],
                },
            },
        },
        legend: {
            enabled: false,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                pointWidth: isCompare ? null : 50,
                minPointLength: 3,
                grouping: true,
                maxPointWidth: 40,
                pointPadding: 0.15,
                pointRange: 1,
                dataLabels: {
                    enabled: !isCompare,
                    crop: false,
                    formatter: function () {
                        return this.point.y ? percentsFilter(this.point.y) : "N/A";
                    },
                    color: "#707070",
                    useHTML: true,
                    style: {
                        fontFamily: '"Roboto", sans-serif',
                        fontSize: "14px",
                        fontWeight: "400",
                    },
                    overflow: "none",
                },
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
        tooltip: {
            enabled: isCompare,
            shared: true,
            outside: true,
            followPointer: true,
            useHTML: true,
            ...tooltipContainerConfig().tooltip,
            formatter: function () {
                const { x, points: socialMediaDataPoints } = this;
                const socialMedia = categories[x] ?? x;
                return ReactDOMServer.renderToString(
                    <Tooltip
                        socialMedia={socialMedia}
                        socialMediaDataPoints={socialMediaDataPoints}
                    />,
                );
            },
        },
    };
    return config;
};
